import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  // Axome - `overflowMenu` is optional so the same component can drive a menu with no overflow list
  // (the secondary "Our commitments" menu rendered in header-actions has only a couple of entries and
  // never overflows). Every overflowMenu access below is already `?.`-guarded, so its absence is a no-op.
  requiredRefs = [];

  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  /**
   * Axome - Whether the page is currently scrolling. The browser fires synthetic
   * pointerenter/leave events as elements move under a stationary cursor while the page
   * scrolls; this flag lets us ignore those so a mega menu the user scrolled past does not
   * re-open by itself when the sticky header slides back into view on scroll-up.
   * @type {boolean}
   */
  #isScrolling = false;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #scrollIdleTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('scroll', this.#scrollListener, { passive: true });
    document.addEventListener('pointermove', this.#trackPointerMovement, { passive: true });
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('scroll', this.#scrollListener);
    document.removeEventListener('pointermove', this.#trackPointerMovement);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    clearTimeout(this.#scrollIdleTimer);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupSubmenuResizeObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Axome - Close any open mega menu as soon as the page scrolls and open a short window
   * during which pointer-triggered activation is ignored (see `#isScrolling`). Without this
   * the panel stays `aria-expanded` while the sticky header is scrolled off-screen, then
   * reappears on scroll-up — sometimes blank, because its content-visibility is culled
   * mid-transition. Keyboard focus still opens the menu normally.
   */
  #scrollListener = () => {
    this.#isScrolling = true;
    clearTimeout(this.#scrollIdleTimer);
    this.#scrollIdleTimer = setTimeout(() => {
      this.#isScrolling = false;
    }, 150);

    if (this.#state.activeItem) {
      this.#deactivate();
    }
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Axome - Timestamp (event clock, ms) of the last GENUINE pointer movement — one where the
   * viewport-relative position actually changed. Scrolling can fire pointerenter/pointermove
   * while the cursor is physically still; those keep the same clientX/clientY and must NOT
   * update this. Used by `activate` to tell a real hover from a scroll-induced enter.
   * @type {number}
   */
  #lastGenuineMoveTime = -Infinity;

  /**
   * Axome - Last position seen by the always-on movement tracker (viewport coords).
   * @type {{ x: number, y: number }}
   */
  #lastTrackedPoint = { x: -1, y: -1 };

  /**
   * Axome - Always-on tracker recording when the pointer genuinely moves. Runs even while no
   * menu is open (unlike `#onPointerMove`) so `activate` can distinguish a real hover from a
   * pointerenter synthesised by the sticky header sliding under a stationary cursor on scroll.
   * @param {PointerEvent} event
   */
  #trackPointerMovement = (event) => {
    if (event.clientX === this.#lastTrackedPoint.x && event.clientY === this.#lastTrackedPoint.y) return;
    this.#lastTrackedPoint.x = event.clientX;
    this.#lastTrackedPoint.y = event.clientY;
    this.#lastGenuineMoveTime = event.timeStamp;
  };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    // Axome - Measure the vertical delta from clientY (fractional) BEFORE updating #lastPointer.
    const dy = event.clientY - this.#lastPointer.y;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    // Axome - Detect downward motion via the fractional clientY delta rather than the
    // integer-rounded `movementY`. A slow drag toward the submenu reports `movementY === 0`,
    // which was misread as "idle": the safety box collapsed mid-crossing and the mega menu
    // closed while the pointer travelled slowly down over the search row toward the level-2
    // links. Horizontal detection keeps using `movementX` so sibling switching is unchanged.
    const moving = Math.abs(event.movementX) >= 1 || dy >= 0.05;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 300);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;

    // Axome - The safety box is the link's `::after`, positioned `top: var(--header-padding)`
    // relative to the link's offset parent (the header row) — NOT relative to the link's own
    // top. Measure the box height from that real top edge so the box always reaches the boundary.
    // Sizing it from the item's top left the box short whenever the link is shorter than the row
    // and vertically centred (the secondary "Our commitments" menu): a ~12px dead zone opened
    // between the box bottom and the submenu top, and pointerleave fired there and closed the
    // menu while the pointer crossed from the entry down to the panel.
    const link = item.querySelector('[ref="menuitem"]') ?? item;
    const anchor = link.offsetParent instanceof HTMLElement ? link.offsetParent : item;
    const afterTop = anchor.getBoundingClientRect().top + (parseFloat(getComputedStyle(link, '::after').top) || 0);
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - afterTop}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Axome - Ignore pointerenter events synthesised by scrolling: when the sticky header slides
    // back under a STATIONARY cursor, the browser fires pointerenter without the cursor actually
    // moving. Suppress only those — a genuine hover (the pointer moved within the last ~120ms)
    // still opens the menu even right after a scroll, so scrolling up the page and moving onto an
    // entry works. Keyboard focus (not a PointerEvent) always activates.
    if (this.#isScrolling && event instanceof PointerEvent && event.timeStamp - this.#lastGenuineMoveTime > 120) {
      return;
    }

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing observer from previous menu activations
      this.#cleanupSubmenuResizeObserver();

      // Axome - Watch the submenu for ANY size change while it's active. A ResizeObserver catches
      // both deferred section hydration (DOM injection) AND late layout shifts like images
      // loading via observer-CFH (which mutate `src` attributes, not childList, so the previous
      // MutationObserver missed them). Updates `--submenu-height` AND `--full-open-header-height`
      // (which drives the submenu clip-path) — keeping only the first would leave the panel
      // clipped at the initial height, hiding any newly-revealed content.
      this.#submenuResizeObserver = new ResizeObserver(() => {
        // Axome - Ignore callbacks delivered after the menu was deactivated (e.g. a reflow caused
        // by opening the cart drawer / any modal). Otherwise the submenu's stale offsetHeight would
        // re-expand `--submenu-height` / `--full-open-header-height` and the empty mega menu
        // background (underlay) would reappear even though the menu is closed.
        if (!this.#state.activeItem) return;
        const h = submenu.offsetHeight;
        if (h <= 0) return;
        this.headerComponent?.style.setProperty('--submenu-height', `${h}px`);
        this.#setFullOpenHeaderHeight(h);
      });
      this.#submenuResizeObserver.observe(submenu);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // Axome - Ignore a spurious pointerleave whose relatedTarget is still inside the active
    // item's own <li>. Moving slowly UP into an entry (cursor coming from the page, across the
    // search row) flips the `moving` flag and toggles the full-width safety-box `::after` under
    // the cursor; the browser then emits a pointerleave on the <li> even though the pointer never
    // left it, closing the menu the instant it opened. A genuine exit — to another entry, the
    // page, or the search bar — has relatedTarget outside this <li> and still closes normally.
    const activeListItem = this.#state.activeItem?.closest('.menu-list__list-item');
    if (
      event.type === 'pointerleave' &&
      event.relatedTarget instanceof Node &&
      activeListItem?.contains(event.relatedTarget)
    ) {
      return;
    }

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    // Axome - Stop watching the (now closed) submenu so a later reflow can't re-apply its open
    // height and resurrect the empty mega menu background. A fresh observer is created on the next
    // activate().
    this.#cleanupSubmenuResizeObserver();

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupSubmenuResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
